import { useState, useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { MemberCombobox } from "@/components/operations/member-combobox";
import { PricePreviewCard } from "@/components/members/price-preview-card";
import { BookingConflictAlert } from "@/components/members/booking-conflict-alert";
// Phase 23-08: useGetMember → useGetAccount (members table dropped)
import {
  useGetAccount,
  getGetAccountQueryKey,
  useListResourcePricing,
  getListResourcePricingQueryKey,
  useCreateBooking,
  useListInventoryPlans,
  useListSpaceResources,
  getListSpaceResourcesQueryKey,
} from "@workspace/api-client-react";
import type {
  BookingConflict,
  SpaceResource,
} from "@workspace/api-client-react";
import { toast } from "sonner";

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
// Phase 10 (Plan 10-07): subtypeLabel helper removed — labels come from plan.planName via useListInventoryPlans.

export default function NewBooking() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const memberIdFromQuery = new URLSearchParams(search).get("memberId");

  const [memberId, setMemberId] = useState<number | null>(
    memberIdFromQuery ? Number(memberIdFromQuery) : null,
  );
  const [resourcePricingId, setResourcePricingId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [discountPct, setDiscountPct] = useState<number>(0);
  const [memberClaimsGst, setMemberClaimsGst] = useState(false);
  const [memberGstin, setMemberGstin] = useState("");
  const [conflicts, setConflicts] = useState<BookingConflict[]>([]);
  const [spaceResourceId, setSpaceResourceId] = useState<number | null>(null);

  // Phase 23-08: useGetMember → useGetAccount (accounts are the canonical billable party)
  const { data: member } = useGetAccount(memberId ?? 0, {
    query: {
      enabled: memberId != null,
      queryKey: getGetAccountQueryKey(memberId ?? 0),
    },
  });

  // Phase 10 (Plan 10-07): pricing filtered by planId; subtype query param removed.
  // Phase 23-08: account doesn't carry planId directly; pricing fetched by spaceId (all active).
  // The server validates plan match at booking time.
  const pricingParams = member
    ? { status: "Active" as const }
    : undefined;

  const { data: pricingRows } = useListResourcePricing(pricingParams, {
    query: {
      enabled: member != null,
      queryKey: getListResourcePricingQueryKey(pricingParams),
    },
  });
  // For plan label rendering
  const { data: plans = [] } = useListInventoryPlans({});
  const planLabel = (id: number) => plans.find((p) => p.id === id)?.planName ?? `Plan #${id}`;

  // Pre-fill GSTIN from member if known
  useEffect(() => {
    if (member?.gstin) setMemberGstin(member.gstin);
  }, [member?.gstin]);

  const selectedPricing = useMemo(
    () => (pricingRows ?? []).find((p) => p.id === resourcePricingId) ?? null,
    [pricingRows, resourcePricingId],
  );

  // Space resources — fetch all resources once a pricing row is chosen (filter Active client-side)
  const { data: spaceResources = [] } = useListSpaceResources(undefined, {
    query: {
      enabled: resourcePricingId != null,
      queryKey: getListSpaceResourcesQueryKey(),
    },
  });

  const RESOURCE_TYPE_LABELS: Record<string, string> = {
    Workstation: "Workstation",
    Private_Cabin: "Private Cabin",
    Meeting_Room: "Meeting Room",
    Conference_Room: "Conference Room",
  };

  // Filter available resources: rooms for hourly; workstations/cabins for non-recurring; Active only
  const filteredSpaceResources = useMemo(() => {
    if (!selectedPricing) return [];
    const active = spaceResources.filter((r: SpaceResource) => r.status === "Active");
    const isHourlyBooking = selectedPricing.paymentUnit === "Hour";
    if (isHourlyBooking) {
      return active.filter(
        (r: SpaceResource) =>
          r.resourceType === "Meeting_Room" || r.resourceType === "Conference_Room",
      );
    }
    // Day pass / non-recurring — show Workstation + Private_Cabin
    return active.filter(
      (r: SpaceResource) =>
        r.resourceType === "Workstation" || r.resourceType === "Private_Cabin",
    );
  }, [selectedPricing, spaceResources]);

  const isHour = selectedPricing?.paymentUnit === "Hour";
  const effectiveQty = isHour ? quantity : 1;

  const createBooking = useCreateBooking();

  async function onSubmit() {
    if (!memberId || !selectedPricing) return;
    if (memberClaimsGst && memberGstin && !GSTIN_REGEX.test(memberGstin)) {
      toast.error("Invalid GSTIN format. Leave blank if not applicable.");
      return;
    }
    try {
      const result = await createBooking.mutateAsync({
        data: {
          memberId,
          resourcePricingId: selectedPricing.id,
          quantity: effectiveQty,
          discountPct,
          memberClaimsGst,
          spaceResourceId: spaceResourceId ?? undefined,
        },
      });
      setConflicts(result.conflicts ?? []);
      toast.success("Booking saved. Mark it Paid when payment is received.");
      navigate(`/accounts/${memberId}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error(
          "The selected seat/room is already taken for this date or slot — choose another resource.",
        );
        // Keep form open so operator can pick another resource
        return;
      }
      toast.error(
        `Booking failed: ${(err as Error).message ?? "Unknown error"}. Try again.`,
      );
    }
  }

  const resourceLabel = selectedPricing
    ? `${planLabel(selectedPricing.planId)} (${selectedPricing.cadence})`
    : null;
  const noPricing = member && (pricingRows?.length ?? 0) === 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">New Booking</h1>

      <div className="space-y-4">
        {/* Field 1: Member */}
        <div>
          <Label>Member</Label>
          <MemberCombobox
            value={memberId}
            onChange={setMemberId}
            memberCategory="Non_Recurring"
          />
        </div>

        {/* Field 2: Resource */}
        <div>
          <Label>Resource</Label>
          <Select
            value={resourcePricingId?.toString() ?? ""}
            onValueChange={(v) => setResourcePricingId(Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a resource pricing row" />
            </SelectTrigger>
            <SelectContent>
              {(pricingRows ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id.toString()}>
                  {planLabel(p.planId)} — {p.cadence}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {noPricing && (
            <Alert className="mt-2">
              <AlertDescription>
                No pricing configured for this account.
                Set up pricing in Settings &gt; Resource Pricing.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Field 2b: Space Resource Picker — shown when a pricing row is selected */}
        {selectedPricing && (
          <div>
            <Label>Assign Seat / Room</Label>
            <Select
              value={spaceResourceId != null ? String(spaceResourceId) : "__none__"}
              onValueChange={(v) =>
                setSpaceResourceId(v === "__none__" ? null : Number(v))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Optional — no seat assigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No seat / room —</SelectItem>
                {filteredSpaceResources.map((r: SpaceResource) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.label} ({RESOURCE_TYPE_LABELS[r.resourceType] ?? r.resourceType})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filteredSpaceResources.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                No available resources for this booking type. Add resources in Inventory → Space.
              </p>
            )}
          </div>
        )}

        {/* Field 3: Quantity */}
        <div>
          <Label>{isHour ? "Hours" : "Quantity"}</Label>
          {isHour ? (
            <Input
              type="number"
              min={1}
              max={12}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-20 h-9"
            />
          ) : (
            <div className="text-sm text-muted-foreground">1 (Fixed quantity)</div>
          )}
        </div>

        {/* Field 4: Discount % */}
        <div>
          <Label>Discount %</Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={discountPct}
            onChange={(e) => setDiscountPct(Number(e.target.value))}
            className="w-20 h-9"
          />
        </div>

        {/* Field 5: Member claims GST toggle */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Switch checked={memberClaimsGst} onCheckedChange={setMemberClaimsGst} />
            <Label>Member claims GST</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Show member&apos;s GSTIN on receipt
          </p>
        </div>

        {/* Field 6: GSTIN (conditional) */}
        {memberClaimsGst && (
          <div>
            <Label>Member GSTIN</Label>
            <Input
              value={memberGstin}
              onChange={(e) => setMemberGstin(e.target.value)}
              placeholder="22AAAAA0000A1Z5"
            />
          </div>
        )}
      </div>

      {/* Price Preview Card — client-side, no network */}
      <PricePreviewCard
        basePrice={selectedPricing ? Number(selectedPricing.basePrice) : null}
        paymentUnit={selectedPricing?.paymentUnit ?? null}
        quantity={effectiveQty}
        discountPct={discountPct}
        resourceLabel={resourceLabel}
      />

      {/* Conflict warning (amber Alert per D-19) */}
      <BookingConflictAlert conflicts={conflicts} />

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => navigate(memberId ? `/accounts/${memberId}` : "/accounts")}
        >
          Discard
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!memberId || !selectedPricing || createBooking.isPending}
        >
          {createBooking.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            "Save Booking"
          )}
        </Button>
      </div>
    </div>
  );
}
